import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { getFirebaseBackend, initFirebaseBackend } from '../../authUtils';
import { User } from 'src/app/store/Authentication/auth.models';
import { from, map, switchMap, of, BehaviorSubject, Observable, firstValueFrom } from 'rxjs';
import {
    Auth,
    signInWithPopup,
    OAuthProvider,
    authState,
    sendSignInLinkToEmail,
    isSignInWithEmailLink,
    signInWithEmailLink,
    User as FirebaseUser
} from '@angular/fire/auth';
import { environment } from '../../../environments/environment';
import { Functions } from '@angular/fire/functions';
import { httpsCallable } from './telemetry';
import { OrgContextService } from './org-context.service';
import { PushNotificationService } from './push-notification.service';

@Injectable({ providedIn: 'root' })

export class AuthenticationService {
    private SESSION_DURATION_MS = 60 * 60 * 1000; // 1 hour
    private backendInitialized = false;
    private auth = inject(Auth);
    private functions = inject(Functions);
    private orgContext = inject(OrgContextService);
    private pushNotifications = inject(PushNotificationService);
    private readonly clearOIDCTokensFn = httpsCallable(this.functions, 'pcoClearOIDCTokens');
    private readonly planningCenterAPIProxyFn = httpsCallable(this.functions, 'pcoPlanningCenterAPIProxy');
    private readonly fetchPlanningCenterOrgFn = httpsCallable(this.functions, 'pcoFetchPlanningCenterOrganization');

    // Current user state - no client-side token storage
    private currentUserSubject = new BehaviorSubject<User | null>(null);

    // Public observables for state changes
    public currentUser$ = this.currentUserSubject.asObservable();
    public authState$: Observable<FirebaseUser | null> = authState(this.auth);


    constructor(private http: HttpClient) {
        // Subscribe to Firebase's auth state to keep our custom user object in sync
        this.authState$.subscribe(fbUser => {
            if (fbUser) {
                // Map FirebaseUser to our app's custom User model
                const appUser: User = {
                    uid: fbUser.uid,
                    // Firebase returns null for an unset field; our User model
                    // uses optional properties.
                    email: fbUser.email ?? undefined,
                    username: fbUser.displayName ?? undefined,
                    // Add any other fields your User model needs
                };
                this.currentUserSubject.next(appUser);
            } else {
                this.currentUserSubject.next(null);
            }
        });
    }

    /**
     * Ensure Firebase backend is initialized
     * This is called lazily to ensure Angular Fire has initialized first
     */
    private ensureBackendInitialized() {
        if (!this.backendInitialized) {
            console.log('Lazy initializing Firebase backend...');
            initFirebaseBackend(environment.firebaseConfig);
            this.backendInitialized = true;
        }
    }

    /**
     * Returns the current user synchronously.
     * Note: Prefer using the currentUser$ observable for reactive scenarios.
     */
    public currentUser(): User | null {
        return this.currentUserSubject.getValue();
    }




    /**
     * Performs the auth
     * @param email email of user
     * @param password password of user
     */
    login(email: string, password: string) {
        this.ensureBackendInitialized();
        // loginUser returns a Promise, so the pipe has to be on the Observable
        // wrapping it — calling .pipe on the Promise itself would have thrown.
        return from(getFirebaseBackend()!.loginUser(email, password)).pipe(
            map((user: any) => {
                this.currentUserSubject.next(user);
                return user as User;
            })
        );
    }

    /**
     * Login with OIDC Provider using Angular Fire Auth directly
     * This ensures Firestore has immediate auth context
     */
    loginWithOIDC() {
        console.log('Using Angular Fire Auth for OIDC login...');
        const provider = new OAuthProvider('oidc.planningcenteronline.com');
        provider.setCustomParameters({ "prompt": "select_account" });
        provider.addScope('groups');
        provider.addScope('people');
        provider.addScope('registrations');
        provider.addScope('services');

        return from(signInWithPopup(this.auth, provider).then(async result => {
            console.log('Angular Fire OIDC login successful!', result.user);
            // The onAuthStateChanged listener will automatically update the currentUserSubject
            return result.user;
        }));
    }

    // -----------------------------------------------------------------------
    // Standalone sign-in — organisations without Planning Center
    //
    // These carry no organisation claim, so membership comes from Firestore.
    // The beforeSignIn blocking function handles that; nothing extra is
    // needed here beyond getting the user authenticated.
    // -----------------------------------------------------------------------

    /**
     * Sign in with Microsoft (Entra ID).
     */
    loginWithMicrosoft() {
        const provider = new OAuthProvider('microsoft.com');
        // Always show the account chooser: people signing in to a work tool
        // frequently have a personal and a work Microsoft account.
        provider.setCustomParameters({ prompt: 'select_account' });
        provider.addScope('email');
        provider.addScope('profile');

        return from(signInWithPopup(this.auth, provider).then(result => result.user));
    }

    /**
     * Send a passwordless sign-in link.
     *
     * The address is stashed in localStorage because completing the link in the
     * same browser lets us skip re-prompting for it. Opening the link on a
     * different device is still supported — completeEmailSignIn asks then.
     */
    async sendEmailSignInLink(email: string, returnUrl?: string): Promise<void> {
        const trimmed = email.trim().toLowerCase();
        const url = new URL(`${window.location.origin}/auth/email-signin`);
        if (returnUrl) url.searchParams.set('returnUrl', returnUrl);

        await sendSignInLinkToEmail(this.auth, trimmed, {
            url: url.toString(),
            handleCodeInApp: true
        });

        window.localStorage.setItem('echoEmailSignIn', trimmed);
    }

    /** Whether the current URL is a Firebase email sign-in link. */
    isEmailSignInLink(link: string): boolean {
        return isSignInWithEmailLink(this.auth, link);
    }

    /** The address the link was sent to, if it was requested in this browser. */
    getStoredSignInEmail(): string | null {
        return window.localStorage.getItem('echoEmailSignIn');
    }

    /**
     * Complete a passwordless sign-in.
     * @param email The address the link was sent to.
     * @param link The full URL that was opened.
     */
    async completeEmailSignIn(email: string, link: string): Promise<FirebaseUser> {
        const result = await signInWithEmailLink(this.auth, email.trim().toLowerCase(), link);
        window.localStorage.removeItem('echoEmailSignIn');
        return result.user;
    }

    /**
     * Performs the register
     * @param email email
     * @param password password
     */
    register(user: User) {
        return from(getFirebaseBackend()!.registerUser(user.email ?? '', (user as any).password ?? '').then((response: any) => {
            const user = response;
            return user;
        }));
    }

    /**
     * Reset password
     * @param email email
     */
    resetPassword(email: string) {
        return getFirebaseBackend()!.forgetPassword(email).then((response: any) => {
            const message = response.data;
            return message;
        });
    }

    /**
     * Logout the user and clear server-side tokens
     */
    async logout() {
        try {
            // Clear server-side stored OIDC tokens. Only Planning Center
            // sign-ins ever create them, so skip the round trip for anyone else.
            const usedPco = this.auth.currentUser?.providerData
                ?.some(p => p.providerId === 'oidc.planningcenteronline.com');

            if (usedPco) {
                try {
                    await this.clearOIDCTokensFn({});
                    console.log('Server-side tokens cleared');
                } catch (tokenError) {
                    console.warn('Failed to clear server-side tokens:', tokenError);
                    // Don't fail logout if token cleanup fails
                }
            }

            // Drop this device's push token while we are still authenticated —
            // the Firestore rules only let a user delete their own token docs.
            await this.pushNotifications.cleanupOnLogout();

            // Sign out from Angular Fire Auth
            await this.auth.signOut();
            this.orgContext.reset();

            // The onAuthStateChanged listener will automatically clear the currentUserSubject
            console.log('User logged out successfully');
        } catch (error) {
            console.error('Error during logout:', error);
            throw error;
        }
    }

    /**
     * Generic method to call Planning Center APIs through server-side functions
     * All OIDC authentication is handled server-side
     * Returns an Observable for compatibility with existing service methods
     */
    callPlanningCenterAPI(endpoint: string, method: 'GET' | 'POST' | 'PUT' | 'DELETE' = 'GET', data?: any): Observable<any> {
        return from(this.planningCenterAPIProxyFn({
            endpoint,
            method,
            data: data || null
        }).then(result => result.data));
    }

    /**
     * Fetch Planning Center organization details
     * Returns organization info including avatar, website, country, date format, and timezone
     */
    fetchPlanningCenterOrganization() {
        return new Promise(async (resolve, reject) => {
            try {
                const currentUser = this.auth.currentUser;

                if (!currentUser) {
                    reject('No authenticated user');
                    return;
                }

                const orgData = await this.fetchPlanningCenterOrgFn({});

                console.log('Fetched organization via server-side OIDC:', orgData.data);
                resolve(orgData.data);
            } catch (error) {
                console.error('Error in fetchPlanningCenterOrganization:', error);
                reject(error);
            }
        });
    }
}
